// @flow weak

import Router from 'next/router';
import getConfig from 'next/config';
import { get, partial } from 'lodash';

import { isBrowser, isProduction } from '@zapier/common-utils';

import type { RouterProps } from 'web-ssr/app/common/next/types';

const nextConfig = getConfig();

const CDN_ASSET_PREFIX =
  get(nextConfig, ['publicRuntimeConfig', 'CDN_ASSET_PREFIX']) || '';

// Used for static assets, e.g. images, anything stored under <root_dir>/static
export const buildStaticAssetPath = url => {
  const prefix = isProduction()
    ? `${CDN_ASSET_PREFIX}/_next/static`
    : `/_next/static`;

  // Prevent duplicated "//" which only cause problems in production
  return `${prefix}/${url.replace(/^\//, '')}`;
};

export const getPageParams = (
  param: string,
  routerProps: RouterProps,
  defaultValue?: any
) => get(routerProps, ['query', param], defaultValue);

export const getSelectedAppSlug = partial(getPageParams, 'slug');
export const getBlogSlug = partial(getPageParams, 'blogSlug');
export const getPrimaryAppSlug = partial(getPageParams, 'primarySlug');
export const getSecondaryAppSlug = partial(getPageParams, 'secondarySlug');
export const getZapId = partial(getPageParams, 'zapId');
export const getSharedZapLinkHash = partial(getPageParams, 'linkHash');

export const redirectClient = (context, target, statusCode = 301) => {
  if (context.res) {
    // On the server-side we need to redirect the response
    context.res.redirect(statusCode, target);
  } else {
    // On the client-side we can leverage the Router
    Router.replace(target);
  }
};

export const stripHtmlTagsFromString = (htmlString: string) => {
  let str = '';

  try {
    if (isBrowser()) {
      const div = document.createElement('div');
      div.innerHTML = htmlString;

      str = div.textContent || div.innerText || '';
    } else {
      // This module should only appear on the server-side, it is ignored on the
      // client-side in `next.config.js` using webpacks `IgnorePlugin`
      // See https://arunoda.me/blog/ssr-and-server-only-modules for more info
      const sanitizeHtml = require('striptags');

      str = sanitizeHtml(htmlString);
    }
  } catch (e) {
    return str;
  }

  // Replace new-lines with a space and trim
  return str.replace(/\r?\n|\r/g, ' ').trim();
};

export const is3xxStatus = (status?: number) =>
  status && status > 299 && status < 400;
export const is4xxStatus = (status?: number) =>
  status && status > 399 && status < 500;
export const is5xxStatus = (status?: number) =>
  status && status > 499 && status < 600;
